import { Color3, type Mesh, MeshBuilder, type Scene } from "@babylonjs/core";
import { CONSTRUCTS, type TargetSpec, type Vec3 } from "@rivalwatch/shared";
import { glow } from "./materials.ts";

/**
 * The local player's aimed ability: a ground ring where it will land and, for a deployable, a
 * ghost of it at its real size facing the way it will be placed. Only you see it, as in Rivals.
 */
export class TargetPreview {
  private ring?: Mesh;
  private disc?: Mesh;
  private ghost?: Mesh;
  private key = "";

  constructor(private readonly scene: Scene) {}

  show(spec: TargetSpec, pos: Vec3, yaw: number, color: string) {
    const key = `${spec.radius ?? 0}:${spec.construct ?? -1}:${color}`;
    if (key !== this.key) {
      this.dispose();
      this.key = key;
      this.build(spec, color);
    }
    const lift = 0.05;
    this.ring?.position.set(pos.x, pos.y + lift, pos.z);
    this.disc?.position.set(pos.x, pos.y + lift * 0.6, pos.z);
    if (this.ghost) {
      const h = CONSTRUCTS[spec.construct!]?.shape.h ?? 1;
      this.ghost.position.set(pos.x, pos.y + h / 2, pos.z);
      this.ghost.rotation.y = yaw;
    }
  }

  hide() {
    if (this.key) this.dispose();
  }

  private build(spec: TargetSpec, color: string) {
    const scene = this.scene;
    const shape = spec.construct !== undefined ? CONSTRUCTS[spec.construct]?.shape : undefined;
    // A deployable without an area still gets a small ring so the landing spot reads.
    const radius = spec.radius ?? (shape ? 0 : 0.6);
    if (radius > 0) {
      const ring = MeshBuilder.CreateTorus("aim-ring", { diameter: radius * 2, thickness: Math.min(0.12, 0.04 + radius * 0.01), tessellation: Math.min(96, 24 + Math.round(radius * 8)) }, scene);
      ring.material = glow(scene, color, 0.85);
      const disc = MeshBuilder.CreateDisc("aim-disc", { radius, tessellation: Math.min(96, 24 + Math.round(radius * 8)) }, scene);
      disc.rotation.x = Math.PI / 2;
      disc.material = glow(scene, color, 0.14);
      for (const m of [ring, disc]) m.isPickable = false;
      this.ring = ring;
      this.disc = disc;
    }
    if (shape) {
      const ghost =
        shape.kind === "box"
          ? MeshBuilder.CreateBox("aim-ghost", { width: shape.hw * 2, depth: shape.hd * 2, height: shape.h }, scene)
          : MeshBuilder.CreateCylinder("aim-ghost", { diameter: shape.r * 2, height: shape.h, tessellation: 24 }, scene);
      ghost.material = glow(scene, color, 0.28);
      ghost.isPickable = false;
      ghost.enableEdgesRendering();
      ghost.edgesWidth = 3;
      ghost.edgesColor = Color3.FromHexString(color).toColor4(0.9);
      this.ghost = ghost;
    }
  }

  dispose() {
    for (const m of [this.ring, this.disc, this.ghost]) m?.dispose();
    this.ring = this.disc = this.ghost = undefined;
    this.key = "";
  }
}
